import QRCode from "qrcode";
import { appConfig } from "@/config/appConfig";

/**
 * Generates a QR code data URL from the provided data
 * @param data - The data to encode in the QR code
 * @returns Promise<string> - Data URL of the generated QR code
 */
export const generateQRCodeDataURL = async (data: string): Promise<string> => {
  try {
    return await QRCode.toDataURL(data, {
      width: 200,
      margin: 1,
      color: {
        dark: "#000000",
        light: "#ffffff",
      },
    });
  } catch (error) {
    console.error("Error generating QR code:", error);
    throw error;
  }
};

/**
 * Formats a ticket number with the configured store prefix
 * @param ticketNumber - The ticket number to format
 * @returns Formatted string with prefix (e.g., "BUN12345")
 */
export const formatQRCodeData = (ticketNumber: string | number): string => {
  return `${appConfig.storePrefix}${ticketNumber}`;
};

/**
 * Extracts the ticket number from a QR code data string
 * Handles both prefixed (BUN12345) and non-prefixed (12345) formats for backward compatibility
 * @param qrData - The scanned QR code data
 * @returns The extracted ticket number or the original data if no prefix found
 */
export const extractTicketNumber = (qrData: string): string => {
  // Check if the data starts with the configured prefix
  if (qrData.startsWith(appConfig.storePrefix)) {
    return qrData.substring(appConfig.storePrefix.length);
  }
  // Return original data for backward compatibility (old QR codes without prefix)
  return qrData;
};

/**
 * Generates a QR code with the store prefix and ticket number
 * @param ticketNumber - The ticket number to encode
 * @returns Promise<string> - Data URL of the generated QR code
 */
export const generateTicketQRCode = async (
  ticketNumber: string | number
): Promise<string> => {
  const formattedData = formatQRCodeData(ticketNumber);
  return generateQRCodeDataURL(formattedData);
};
